import { ForumAdminBadge } from '@/components/forum/forum-badges'
import {
  localizedBoardName,
  pickTranslation,
} from '@/components/forum/forum-i18n'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { getArenaUrl, isArenaHost } from '@/lib/arena'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'
import { isSteamClient } from '@/lib/steam'
import { withLanguagePath } from '@/lib/url-language-utils'
import { cn, formatDate } from '@/lib/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, History, Inbox, MailOpen } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

export const FORUM_INBOX_LIMIT = 50

type InboxNotification = {
  id: string
  createdAt: Date | string
  readAt: Date | string | null
  type: string
  thread: {
    id: string
    title: string
    titleTranslations?: Record<string, string> | null
    body: string
    bodyTranslations?: Record<string, string> | null
    createdAt: Date | string
    board: { slug: string, name: string }
    authorName: string
    isAdminAuthor: boolean
  }
  reply: {
    id: string
    body: string
    createdAt: Date | string
    authorName: string
    isAdminAuthor: boolean
  } | null
}

function notificationPath(notification: InboxNotification) {
  const replyHash = notification.reply ? `#reply-${notification.reply.id}` : ''
  return `/forum/thread/${notification.thread.id}${replyHash}`
}

function notificationDate(notification: InboxNotification) {
  return notification.reply?.createdAt ?? notification.thread.createdAt
}

function notificationPreview(
  notification: InboxNotification,
  language: string,
) {
  const localizedThreadBody
    = notification.thread.bodyTranslations?.[language] ?? notification.thread.body
  const body = notification.reply?.body.trim() || localizedThreadBody.trim()
  return body
    .replaceAll(/<[^>]*>/g, ' ')
    .replaceAll(/\s+/g, ' ')
    .trim()
}

export function InboxTab() {
  const { user } = useAuth()
  const { t, language } = useI18n()
  const trpc = useTRPC()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showHistory, setShowHistory] = useState(false)
  const queryOptions = trpc.forumRouter.listNotifications.queryOptions({
    limit: FORUM_INBOX_LIMIT,
    includeRead: showHistory,
  })

  const { data, isLoading, isError, refetch } = useQuery({
    ...queryOptions,
    enabled: !!user,
    refetchInterval: 30_000,
  })
  const markRead = useMutation({
    ...trpc.forumRouter.markNotificationRead.mutationOptions(),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.forumRouter.listNotifications.queryKey(),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.forumRouter.unreadNotificationCount.queryKey(),
      })
    },
  })

  const notifications = data?.notifications ?? []
  const unreadCount = data?.unreadCount ?? 0

  const openForumPath = (path: string) => {
    const destination = new URL(path, window.location.origin)
    destination.pathname = withLanguagePath(destination.pathname, 'en')

    const arenaUrl = getArenaUrl()
    if (arenaUrl) {
      window.location.assign(
        `${arenaUrl}${destination.pathname}${destination.search}${destination.hash}`,
      )
      return
    }

    if (isSteamClient()) {
      navigate(path)
      return
    }

    if (!isArenaHost()) {
      const separator = destination.search ? '&' : '?'
      window.location.assign(
        `${window.location.origin}${destination.pathname}${destination.search}${separator}arena${destination.hash}`,
      )
      return
    }

    window.location.assign(destination.toString())
  }

  const openNotification = async (notification: InboxNotification) => {
    if (!notification.readAt) {
      await markRead
        .mutateAsync({ notificationId: notification.id })
        .catch(() => {})
    }
    openForumPath(notificationPath(notification))
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground">
            <Inbox className="size-5" />
            {t('auth.dashboard.inbox')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('forum.inboxDescription')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {unreadCount > 0 && (
            <Badge variant="secondary" className="rounded-full">
              {t('forum.inboxUnreadCount', { count: unreadCount })}
            </Badge>
          )}
          <Button
            type="button"
            variant={showHistory ? 'secondary' : 'outline'}
            size="sm"
            className="gap-2"
            onClick={() => setShowHistory(value => !value)}
          >
            <History className="size-4" />
            {showHistory
              ? t('forum.inboxHideHistory')
              : t('forum.inboxShowHistory')}
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
          {t('forum.inboxLoading')}
        </div>
      )}

      {isError && (
        <div className="rounded-lg border border-border bg-card p-6">
          <p className="text-sm text-muted-foreground">
            {t('forum.inboxError')}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => {
              refetch().catch(() => {})
            }}
          >
            {t('forum.inboxRetry')}
          </Button>
        </div>
      )}

      {!isLoading && !isError && notifications.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-card p-8 text-center">
          <MailOpen className="mx-auto size-8 text-muted-foreground" />
          <h3 className="mt-3 text-base font-semibold text-foreground">
            {showHistory ? t('forum.inboxEmpty') : t('forum.inboxUnreadEmpty')}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {showHistory
              ? t('forum.inboxEmptyDescription')
              : t('forum.inboxUnreadEmptyDescription')}
          </p>
        </div>
      )}

      {notifications.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="divide-y divide-border">
            {notifications.map(notification => (
              <button
                key={notification.id}
                type="button"
                aria-label={t('forum.inboxOpen')}
                className={cn(
                  'grid w-full grid-cols-[1fr_auto] gap-3 px-4 py-3 text-left transition hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  !notification.readAt && 'bg-primary/5',
                )}
                onClick={() => {
                  openNotification(notification).catch(() => {})
                }}
              >
                <div className="min-w-0">
                  {
                  }
                  <div className="flex items-start gap-2">
                    {!notification.readAt && (
                      <span
                        className="mt-1.5 size-2 shrink-0 rounded-full bg-primary"
                        aria-hidden="true"
                      />
                    )}
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <span className="break-words text-sm font-semibold text-foreground">
                        {notification.reply
                          ? t('forum.inboxReplyTitle', {
                              author: notification.reply.authorName,
                              title:
                                pickTranslation(
                                  notification.thread.titleTranslations,
                                  language,
                                ) ?? notification.thread.title,
                            })
                          : t('forum.inboxAdminThreadTitle', {
                              title:
                                pickTranslation(
                                  notification.thread.titleTranslations,
                                  language,
                                ) ?? notification.thread.title,
                            })}
                      </span>
                      {(
                        notification.reply
                          ? notification.reply.isAdminAuthor
                          : notification.thread.isAdminAuthor
                      ) ? (
                            <ForumAdminBadge />
                          ) : null}
                      {showHistory && (
                        <Badge
                          variant={
                            notification.readAt ? 'secondary' : 'default'
                          }
                          className="rounded-full"
                        >
                          {notification.readAt
                            ? t('forum.inboxRead')
                            : t('forum.inboxUnread')}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <p className="mt-1 line-clamp-3 break-words text-sm text-muted-foreground">
                    {notificationPreview(notification, language)
                      || t('forum.inboxNoPreview')}
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t('forum.inboxInBoard', {
                      board: localizedBoardName(t, notification.thread.board),
                      date: formatDate(notificationDate(notification)),
                    })}
                  </p>
                </div>
                <ExternalLink className="mt-1 size-4 text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}
